import { z } from "zod";
import { catalogueLocale } from "../catalog/vocabulary";
import { appLocale } from "../locale";
/**
 * `GET /me/insight?locale=pt` — the Today screen's one insight card (plan
 * D38, T36).
 *
 * ── DETERMINISTIC SERVER RULES, NO LLM ──────────────────────────────────────
 * The rules live in ONE place: `packages/modules/training/src/core/model/
 * insight.ts`. The same data gives the same insight. When several rules
 * fire, the most useful one wins, in {@link INSIGHT_KINDS} order:
 * `pain_checkin > plateau > progress_load > personal_record > consistency`.
 * `insight` is `null` when no rule fires.
 *
 * ── KEYS, NAMES AND NUMBERS — ONE SENTENCE EXCEPTED ─────────────────────────
 * `messageKey` is an `insight.*` key the client renders with `params` (`D27`).
 * `params` carry names in the request's `?locale` and numbers. The one
 * sentence on the wire is `ask_trainer.ask`: free text that lands in the
 * trainer's composer, so the server renders it in the request's locale.
 */
/** In priority order — the first is the most useful. */
export const INSIGHT_KINDS = [
    "pain_checkin",
    "plateau",
    "progress_load",
    "personal_record",
    "consistency",
];
export const insightKind = z.enum(INSIGHT_KINDS);
export const insightQuery = z.object({
    /** The locale exercise names and the trainer ask are in. English when
     *  absent. */
    locale: catalogueLocale.optional(),
    /**
     * The interface locale the trainer ask is WRITTEN in (`D267`), when it is
     * not the catalogue's: a Spanish reader gets English exercise names (the
     * catalogue has two languages) and a Spanish ask. Absent, the ask follows
     * `locale` — `pt` is pt-BR, anything else en-US — which is every app
     * built before the field. Optional, and `insightQuery` is not strict, so an
     * older server ignores it rather than refusing it.
     */
    language: appLocale.optional(),
});
export const insightAction = z.discriminatedUnion("type", [
    z.object({ type: z.literal("open_exercise"), exerciseId: z.string().min(1) }),
    z.object({ type: z.literal("ask_trainer"), ask: z.string().min(1).max(500) }),
    z.object({ type: z.literal("open_plan") }),
    z.object({ type: z.literal("open_checkin"), sessionId: z.string().min(1) }),
]);
export const insight = z.object({
    kind: insightKind,
    /** An `insight.*` key. */
    messageKey: z.string().min(1),
    params: z.record(z.string(), z.union([z.string(), z.number()])),
    action: insightAction,
});
export const insightOutput = z.object({
    /** `null` when no rule fires. */
    insight: insight.nullable(),
});
//# sourceMappingURL=insight.js.map